import * as React from 'react'

import { Button, Delayed, Icon, Spinner, useTour } from '../../components'

interface TourDefinition {
  id: string
  steps: Array<{ title?: React.ReactNode; body: React.ReactNode; target?: string; placement?: string }>
  skip?: boolean
}

// If we need to update / discontinue some tour we can use skip flag on legacy version to ensure that it won't reset users tour but won't include it in new tours
const TOURS: TourDefinition[] = [
  {
    id: 'page-tour',
    steps: [
      {
        title: 'Welcome to new Charts',
        body: 'We’ve moved some buttons and cleaned up a bit so there’s more space for your charts and new upcoming features.',
      },
      {
        target: 'header .menu-button',
        placement: 'left-start',
        title: 'Menu',
        body: 'Links and search are now hidden when you don’t need them.',
      },
      {
        target: 'header',
        body: (
          <>Choose from different chart layouts, switch to dark mode, enter fullscreen or publish your&nbsp;chart.</>
        ),
      },
      {
        target: '[data-testid="chart-toolbar-ideas"]',
        placement: 'bottom-start',
        title: 'Save and load ideas',
        body: (
          <>
            You can now save and load ideas right from Charts. Just select one of the charts and click
            Ideas&nbsp;&#8594;&nbsp;Save.
          </>
        ),
      },
      {
        target: '.zoomer',
        title: 'Zoom',
        body: 'Drag the X-scale to zoom. Double-click to reset.',
      },
      {
        target: '.scaler',
        title: 'Pan',
        body: 'Drag the Y-scale to change it. With custom scale, you can now pan the chart up and down.',
      },
    ],
  },
  {
    id: 'settings',
    steps: [
      {
        target: '.settings-button',
        title: 'Study Settings',
        body: 'Open the study settings to customize chart type, add overlays and indicators, or toggle extended hours directly from charts.',
      },
    ],
  },
  {
    id: 'chart-sync',
    steps: [
      {
        target: '[data-testid="chart-layout-layout"]',
        title: 'Sync settings',
        body: 'Open the layout settings to synchronize Ticker, Timeframe and Crosshair position across your charts.',
      },
    ],
  },
  {
    id: 'preserve-drawings',
    steps: [
      {
        target: '[data-testid="toolbar-item-preserve-drawings"]',
        title: 'Preserve drawings',
        body: 'Preserve drawings while changing timeframes.',
      },
    ],
    skip: true,
  },
  {
    id: 'preserve-drawings-new-default',
    steps: [
      {
        target: '[data-testid="toolbar-item-preserve-drawings"]',
        title: 'Preserve drawings',
        body: 'This option is now enabled by default, so you can easily change timeframes without losing your drawings.',
      },
    ],
    skip: true,
  },
]

// Migrate legacy tour to the new route-based system
function migrateLegacyTour(tour: any) {
  const legacyTourKey = 'charts-page-tour-finished'

  if (localStorage.getItem(legacyTourKey)) {
    tour.completeTour(TOURS[0].id)
    localStorage.removeItem(legacyTourKey)
  }

  return tour
}

export function TourButton() {
  const tour = useTour()

  const [userTour, setCurrentTour] = React.useState(migrateLegacyTour(tour).getStepsForUser(TOURS))

  return (
    <Button
      theme={userTour.hasNewTours ? 'blue' : 'headerItem'}
      leftContent={
        tour.isLoading ? (
          <Delayed delayComponent={<Icon name="gift" className="text-white" />}>
            <Spinner width={16} />
          </Delayed>
        ) : (
          <Icon name="gift" className="text-white" />
        )
      }
      className="mobile:hidden"
      contentClass="hidden lg:flex"
      title="Explore new features"
      onClick={async () => {
        if (userTour) {
          const activeTour = await tour.startTour(userTour.tour)
          activeTour?.once('inactive', () => {
            setCurrentTour(tour.getStepsForUser(TOURS))
          })
        }
      }}
    >
      What’s new
    </Button>
  )
}
